import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, session } from "@/lib/api";
import { toast } from "sonner";
import useDashboardData from "@/hooks/useDashboardData";
import {
    Trophy, ChevronRight,
    Crown, BookOpen, Shield, Lock
} from "lucide-react";
import LiveGameStrip from "@/components/LiveGameStrip";
import { useScheduleLive } from "@/hooks/useScheduleLive";
// iter87.0 — One UI: the main page IS a bento grid; tiles open planes.
import BentoHome from "@/components/dashboard/BentoHome";
// iter87.1 — Crew-member To-Dos strip (actionable pick/rank/boost chips).
import TodoChips from "@/components/dashboard/TodoChips";
import { useMatchdayBoost } from "@/components/dashboard/MatchdayBoostPanel";
import { useSquares, isSquaresTemplate } from "@/components/dashboard/SquaresPanel";
import SquaresModal from "@/components/dashboard/SquaresModal";
import { useRivalriesIntel } from "@/lib/useRivalriesIntel";
import { usePlayoffSeriesIntel } from "@/lib/usePlayoffSeriesIntel";
import NflLiveTicker from "@/components/dashboard/NflLiveTicker";
import SoccerLiveTicker from "@/components/dashboard/SoccerLiveTicker";
import MlbSeriesPickModal from "@/components/dashboard/MlbSeriesPickModal";
import MlbChampionsPickModal from "@/components/dashboard/MlbChampionsPickModal";
import NbaSeriesPickModal from "@/components/dashboard/NbaSeriesPickModal";
import NbaChampionsPickModal from "@/components/dashboard/NbaChampionsPickModal";
import NwslChampionPickModal from "@/components/dashboard/NwslChampionPickModal";
import NwslDarkhorsePickModal from "@/components/dashboard/NwslDarkhorsePickModal";
import NflChampionPickModal from "@/components/dashboard/NflChampionPickModal";
import NflDarkhorsePickModal from "@/components/dashboard/NflDarkhorsePickModal";
import CfbChampionPickModal from "@/components/dashboard/CfbChampionPickModal";
import CfbDarkhorsePickModal from "@/components/dashboard/CfbDarkhorsePickModal";
import NwslPredictorModal from "@/components/dashboard/NwslPredictorModal";
import MlsChampionPickModal from "@/components/dashboard/MlsChampionPickModal";
import MlsDarkhorsePickModal from "@/components/dashboard/MlsDarkhorsePickModal";
import MlsPlayinPickModal from "@/components/dashboard/MlsPlayinPickModal";
import MlsSeriesPickModal from "@/components/dashboard/MlsSeriesPickModal";
import UclChampionPickModal from "@/components/dashboard/UclChampionPickModal";
import UclDarkhorsePickModal from "@/components/dashboard/UclDarkhorsePickModal";
import UclAdvancePickModal from "@/components/dashboard/UclAdvancePickModal";
import UclChampionsPredictorModal from "@/components/dashboard/UclChampionsPredictorModal";
import { isApiFootballTemplate } from "@/lib/apiFootballTemplates";
import { myPicksForTournament } from "@/lib/tournamentFormat";
// iter87.0 — DashboardTabs repurposed: plane container (Takeover per tile).
import DashboardTabs from "@/components/dashboard/DashboardTabs";
// iter86.95 — One UI Phase 1: HeroStrip (collapsing hero + floating menu)
// replaces DashboardHeader + the tournament hero card.
import HeroStrip from "@/components/dashboard/HeroStrip";
// iter63z'''''''''''' — Slice 4 of dashboard refactor: all modal mounts.
import DashboardModalStack from "@/components/dashboard/DashboardModalStack";
// iter87.199.68 — AdminFirstRunWizard retired. First-run onboarding
// lives at `/start` now (see `pages/Start.jsx`). The hamburger's
// "Show setup wizard" entry navigates there for admins with no
// tournament yet.
import { setStoredViewTournament } from "@/components/dashboard/TournamentSwitcher";
import CompleteNudgeBanner from "@/components/dashboard/CompleteNudgeBanner";
// iter87.180.1 — PlayerFirstRunWizard auto-open removed; non-admins see
// the Rivalry Manual (UserGuideModal) on first sign-in instead.
import EmailRecoveryBanner from "@/components/dashboard/EmailRecoveryBanner";
// iter87.6 — Prominent call-out for admins to kick off the draft.
import StartDraftBanner from "@/components/dashboard/StartDraftBanner";
import StartBoostTournamentBanner from "@/components/dashboard/StartBoostTournamentBanner";
// iter87.2 — Smack Stream + Rivalry Messages promoted to a permanent
// section at the bottom of the dashboard (below the bento grid).
import DashboardFooterCards from "@/components/dashboard/DashboardFooterCards";
// iter87.3 — Focused Takeovers for chip-driven activities.
import PotPicksModal from "@/components/dashboard/PotPicksModal";
import MatchdayBoostModal from "@/components/dashboard/MatchdayBoostModal";
import RivalriesIntelModal from "@/components/dashboard/RivalriesIntelModal";
import PlayoffSeriesIntelModal from "@/components/dashboard/PlayoffSeriesIntelModal";
import { MessagesSquare } from "lucide-react";
// iter87.180 Item E — Non-admin "Don't forget" nudge above the DRAFT section.
import DontForgetBanner from "@/components/dashboard/DontForgetBanner";

export default function LeagueDashboard() {
    const { leagueId } = useParams();
    const navigate = useNavigate();

    // iter63z'''''''' — Data layer extracted to `useDashboardData` hook. The
    // hook owns: league payload, leaderboard, me, spectator flag, movers,
    // recent changes, season standing, smack ping + unread mentions,
    // tournament switcher state, the throttled `load()` callback, and the
    // WebSocket + 45s polling subscription. The page below is pure UI/UX.
    const {
        data,
        board,
        me,
        setMe,
        isSpectator,
        roundMovers,
        recentChanges,
        seasonStanding,
        wsConnected,
        smackPing,
        unreadMentions,
        setUnreadMentions,
        setViewTournamentId,
        switcherRefreshKey,
        tournamentCount,
        setTournamentCount,
        load,
        resetLoadThrottle,
    } = useDashboardData(leagueId);

    // ─── purely UI state that stays on the page ────────────────────────
    const [copied, setCopied] = useState(false);
    const [knockoutPickModal, setKnockoutPickModal] = useState(false);
    const [powerPickModal, setPowerPickModal] = useState(false);
    const [rankTeamsModal, setRankTeamsModal] = useState(false);
    // iter87.3 — Each To-Do chip opens its OWN focused surface. Pot
    // picks + Matchday Boost used to open the shared My Picks plane
    // (stacked with Power Up etc.); they now open dedicated Takeovers.
    const [potPicksModal, setPotPicksModal] = useState(false);
    const [matchdayBoostModal, setMatchdayBoostModal] = useState(false);
    const [squaresModal, setSquaresModal] = useState(false);
    const [rivalriesIntelModal, setRivalriesIntelModal] = useState(false);
    const [playoffSeriesIntelModal, setPlayoffSeriesIntelModal] = useState(false);
    // iter87.465.26 — MLS's two independently-scoped intel tiles each
    // get their own modal state (see `usePlayoffSeriesIntel.js`).
    const [mlsSeriesIntelModal, setMlsSeriesIntelModal] = useState(false);
    const [mlsKnockoutIntelModal, setMlsKnockoutIntelModal] = useState(false);
    const [mlbChampsModal, setMlbChampsModal] = useState(false);
    const [mlbSeriesModal, setMlbSeriesModal] = useState(false);
    const [nbaChampsModal, setNbaChampsModal] = useState(false);
    const [nbaSeriesModal, setNbaSeriesModal] = useState(false);
    const [nwslChampionModal, setNwslChampionModal] = useState(false);
    const [nwslDarkhorseModal, setNwslDarkhorseModal] = useState(false);
    const [nflChampionModal, setNflChampionModal] = useState(false);
    const [nflDarkhorseModal, setNflDarkhorseModal] = useState(false);
    const [cfbChampionModal, setCfbChampionModal] = useState(false);
    const [cfbDarkhorseModal, setCfbDarkhorseModal] = useState(false);
    const [nwslPredictorModal, setNwslPredictorModal] = useState(false);
    const [mlsChampionModal, setMlsChampionModal] = useState(false);
    const [mlsDarkhorseModal, setMlsDarkhorseModal] = useState(false);
    const [mlsPlayinModal, setMlsPlayinModal] = useState(false);
    const [mlsSeriesModal, setMlsSeriesModal] = useState(false);
    // iter87.466.1 — UCL Playoffs: own pre-season picks + Advance
    // Predictor + Champions Predictor + its two intel tiles.
    const [uclChampionModal, setUclChampionModal] = useState(false);
    const [uclDarkhorseModal, setUclDarkhorseModal] = useState(false);
    const [uclAdvanceModal, setUclAdvanceModal] = useState(false);
    const [uclChampionsPredictorModal, setUclChampionsPredictorModal] = useState(false);
    const [uclTwoLegIntelModal, setUclTwoLegIntelModal] = useState(false);
    const [uclFinalIntelModal, setUclFinalIntelModal] = useState(false);
    const [legalPrivacyOpen, setLegalPrivacyOpen] = useState(false);
    const [legalTermsOpen, setLegalTermsOpen] = useState(false);
    const [view, setView] = useState(null); // iter87.0 — null = bento home; string = open plane
    const [rulesOpen, setRulesOpen] = useState(false);
    // iter31q — player-facing User Guide. Auto-opens once per browser
    // (localStorage flag scoped to leagueId so users see it the first time
    // they visit a league, not every dashboard load).
    const [userGuideOpen, setUserGuideOpen] = useState(false);
    const [emailModalOpen, setEmailModalOpen] = useState(false);
    const [emailDraft, setEmailDraft] = useState("");
    const [emailBannerDismissed, setEmailBannerDismissed] = useState(
        () => localStorage.getItem("tcr_email_banner_dismissed") === "1"
    );
    const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
    // iter63z'''''''''' — "Your Tiny Crew" modal: lists every league participant
    // username + status.
    // iter87.199.5 — Crown Race deprecation: was previously only shown
    // in single-tournament rivalries because multi-tournament rivalries
    // routed to /crown-points. That surface is retired now so this
    // modal is the always-on crewmate roster.
    const [crewModalOpen, setCrewModalOpen] = useState(false);
    // iter84.24 — Universal crew identity (avatar + flair) editor modal.
    // Reachable from the dashboard header pill in BOTH single- and
    // multi-tournament Rivalries.
    const [crewIdentityOpen, setCrewIdentityOpen] = useState(false);
    // iter63z''''''''''''' — First-run guided wizard state. Both auto-open
    // iter87.199.68 — AdminFirstRunWizard retired. `adminWizardOpen`
    // state removed. The hamburger's "Show setup wizard" entry now
    // navigates to `/start` for admins who still don't have a
    // tournament configured.



    const handleCopy = () => {
        navigator.clipboard.writeText(data.league.invite_code);
        setCopied(true);
        toast.success("Invite code copied");
        setTimeout(() => setCopied(false), 2000);
    };

    // iter87.180.1 → iter87.199.68 — Non-admin first-run pop is the
    // Rivalry Manual itself. Admin first-run has moved OFF this
    // dashboard entirely to the `/start` route; there's no wizard to
    // auto-open here anymore. Admins with no tournament yet can
    // navigate to `/start` via the hamburger's "Show setup wizard"
    // entry.
    useEffect(() => {
        if (!data?.league || !me) return;
        const isAdmin = !!me.is_admin;
        if (isAdmin) return; // No auto-open for admins post-iter87.199.68.
        const storageKey = `tcr_player_wizard_seen_${leagueId}`;
        let seen = false;
        try { seen = localStorage.getItem(storageKey) === "1"; } catch (e) { /* ignore */ }
        if (seen) return;
        // Non-admins: pop the Rivalry Manual directly (no wizard).
        setUserGuideOpen(true);
        try { localStorage.setItem(storageKey, "1"); } catch (e) { /* ignore */ }
    }, [data?.league, me, leagueId]);

    // iter63z''' — Click handler for the unread-mention chip in the header.
    // iter87.2 — Smack Stream is now a permanent section at the bottom of
    // the dashboard, so the chip scrolls to it (was: opened a plane).
    const handleMentionChipClick = useCallback(async () => {
        const el = document.getElementById("smack-section");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        setUnreadMentions(0);
        try {
            await api.markSmackSeen(leagueId);
        } catch {
            // Non-fatal — UI count is cleared; persistence will catch up next load.
        }
    }, [leagueId, setUnreadMentions]);

    // iter84.19 — Deep-link support for scrolling to the messages card.
    // iter87.2 — `#league-messages-card` now scrolls to the permanent
    // Smack section (was: opened the Smack plane).
    useEffect(() => {
        if (window.location.hash !== "#league-messages-card") return;
        if (!data) return;
        const t = setTimeout(() => {
            const el = document.querySelector('[data-testid="league-messages-card"]')
                || document.getElementById("smack-section");
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 400);
        return () => clearTimeout(t);
    }, [data]);

    const handleLogout = () => {
        setLogoutConfirmOpen(true);
    };

    const confirmLogout = () => {
        session.clear();
        navigate("/");
    };

    const handleSaveEmail = async (rawEmail) => {
        const email = (rawEmail ?? emailDraft).trim().toLowerCase();
        if (!email || !email.includes("@")) {
            toast.error("Enter a valid email");
            return;
        }
        try {
            const updated = await api.updateParticipantEmail(me.id, email);
            setMe(updated);
            toast.success("Recovery email saved");
            setEmailModalOpen(false);
            setEmailDraft("");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to save email");
        }
    };

    const dismissEmailBanner = () => {
        localStorage.setItem("tcr_email_banner_dismissed", "1");
        setEmailBannerDismissed(true);
    };

    const handleToggleNotifications = async (enabled) => {
        try {
            const updated = await api.updateNotificationPreferences(me.id, enabled);
            setMe(updated);
            toast.success(enabled ? "Email notifications on" : "Email notifications off");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to update notification preference");
        }
    };

    const handleSetKnockoutPick = async (teamId) => {
        try {
            // iter63z'''' — Pass the currently-viewed tournament_id so a
            // pick in tournament A doesn't overwrite tournament B's pick.
            // Backend defaults to league.active_tournament_id when omitted.
            await api.setKnockoutPick({
                participant_id: me.id,
                team_id: teamId,
                tournament_id: tournament?.id,
            });
            toast.success("Bonus pick saved");
            setKnockoutPickModal(false);
            load();
        } catch (err) {
            toast.error("Failed");
        }
    };

    // iter35d — Power Pick handler
    const handleSetPowerPick = async (teamId) => {
        try {
            await api.setPowerPick(leagueId, me.id, teamId, data?.tournament?.id);
            toast.success("Power Pick saved — get the edge!");
            setPowerPickModal(false);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
    };

    // Memoized derived values — must be declared BEFORE any early return per rules-of-hooks
    const tournament = data?.tournament;
    // iter62 — Single source of truth for "is anything live right now?".
    // Drives the Schedule-tab badge, the inline LiveGameStrip, and pauses
    // its 75s polling on hidden tabs / when nothing is live.
    const { liveEntries, liveCount, tickerEntries } = useScheduleLive(tournament?.id);
    // iter87.212.31 — Templates that already render a detailed live-score
    // card block above (football's NflLiveTicker, soccer's new
    // SoccerLiveTicker) don't also need the scrolling LiveGameStrip —
    // that's redundant once the detailed cards exist for a sport.
    // iter87.312 (user-reported, 3rd time — "the live scrolling strip
    // is suppressed [for NFL] and it is a different view... 2 teams
    // stacked on top of each other with scores and quarter/time to the
    // right, in a box") — MLB/NBA Playoffs were never added to this
    // list, so they alone stayed stuck on the plain scrolling ticker
    // while every other sport got the detailed card treatment years ago.
    const MLB_NBA_BRACKET_TEMPLATES = ["mlb_playoffs_api", "nba_playoffs_api"];
    const hasDetailedLiveTicker = ["nfl_season_api", "nfl_boost_api", ...MLB_NBA_BRACKET_TEMPLATES].includes(tournament?.template)
        || isApiFootballTemplate(tournament?.template);
    // iter87.1 — Matchday Boost lifted to page-level so the TodoChips
    // strip can surface its live/open/locked status without duplicating
    // the poll. `DashboardTabs` receives the same shape through the
    // plane view (still fetched inside on-demand today; consolidating
    // later if we see two live requests in practice).
    const boost = useMatchdayBoost(tournament, me?.id);
    // iter87.550 — Squares (NFL/CFB single-game board), same page-level
    // lift as Matchday Boost above so TodoChips sees live status.
    const squares = useSquares(tournament, me?.id);
    // iter87.47 Slice C — Rivalries Intel v2 tile (Premium-gated at API).
    // iter87.49 — Now tournament-scoped so mixed-sport leagues render
    // the right sport on each tournament's tile.
    const intel = useRivalriesIntel(tournament);
    const playoffSeriesIntel = usePlayoffSeriesIntel(tournament);
    const myRow = useMemo(() => board.find(r => r.participant_id === me?.id), [board, me?.id]);
    const myTeams = useMemo(
        () => me?.assigned_team_ids?.map(tid => tournament?.teams.find(t => t.id === tid)).filter(Boolean) || [],
        [me?.assigned_team_ids, tournament?.teams]
    );
    // Standings tab visibility — only formats with group or season play
    const showStandingsTab = useMemo(() => {
        if (!tournament) return false;
        const fmt = tournament.format;
        const tpl = tournament.template;
        // iter86.31.1 — Golf gets a Standings tab too (full-field golfer
        // standings, rendered by GolfStandings instead of GroupStandings).
        if (fmt === "golf") return true;
        return (
            tpl === "world_cup" || tpl === "world_cup_api" || tpl === "olympics" || tpl === "football_playoffs" ||
            fmt === "group_knockout" || fmt === "round_robin"
        );
    }, [tournament]);
    // Bracket tab visibility — every format EXCEPT pure round-robin / season-only,
    // UNLESS the tournament has knockout stages configured (e.g. GotSport-imported
    // custom tournaments default to format='round_robin' but still have SF/Final).
    const showBracketTab = useMemo(() => {
        if (!tournament) return false;
        // iter87.550 — Squares is a single real-game board, no bracket.
        if (isSquaresTemplate(tournament.template)) return false;
        const fmt = tournament.format;
        // iter86.12 — 'champion' is a virtual award stage (season engine
        // uses [group, champion]) — it is NOT a knockout round. Without
        // this exclusion every round_robin season template leaked an
        // empty Bracket tab.
        const hasKnockoutStage = (tournament.stages || []).some(
            s => s.key && s.key !== "group" && s.key !== "champion",
        );
        if (hasKnockoutStage) return true;
        // iter86.31 — Golf: strokes/points engine, no bracket ever.
        if (fmt === "golf") return false;
        // Hide for season-only formats with no KO configured.
        return fmt !== "round_robin";
    }, [tournament]);
    // If the open plane's tile gets hidden (e.g. admin reconfigures the
    // format while a plane is open), snap back to the bento home.
    useEffect(() => {
        if (view === "bracket" && !showBracketTab) setView(null);
        if (view === "standings" && !showStandingsTab) setView(null);
        // iter86.31 — Golf has no Schedule plane; snap back defensively.
        if (view === "schedule" && tournament?.format === "golf") setView(null);
        // iter45g — Crown view is a separate page; snap back (defensive).
        if (view === "crown") setView(null);
    }, [view, showBracketTab, showStandingsTab, tournament?.format]);

    // iter73 — Removed legacy "New here? Check out Your Rivalry Manual" toast.
    // The PlayerFirstRunWizard / AdminFirstRunWizard now own first-run
    // orientation, and the wizard's close handler surfaces the manual
    // reminder. Keeping a no-op effect would be confusing; deleted.
    // iter86.96 — Dark Horses card eliminated with the One UI Phase 2
    // culling (operator decision); dangerHorses memo removed with it.
    const eliminatedTeams = useMemo(
        () => (tournament?.teams || []).filter(t => t.eliminated),
        [tournament?.teams]
    );
    const champion = useMemo(
        () => tournament?.teams?.find(t => t.stage_reached === "champion"),
        [tournament?.teams]
    );
    const availableTeams = useMemo(
        () => (tournament?.teams || []).filter(t => !t.eliminated),
        [tournament?.teams]
    );

    if (!data) {
        return <div className="min-h-screen flex items-center justify-center text-zinc-500">Loading...</div>;
    }

    const { league, draft, participants } = data;

    // iter84.24 — Find the current user's participant doc to feed
    // avatar/flair into the header pill + identity modal. Falls back to
    // an empty object so the header still renders for spectators.
    const myParticipant = (me?.id && (participants || []).find(p => p.id === me.id)) || null;

    return (
        <div className="min-h-screen pb-24 lg:pb-16">
            {/* iter86.95 — One UI Phase 1: HeroStrip = collapsing hero
                (name + status pill + switcher in one sticky glass row,
                pills fold into an expandable detail) + the floating glass
                menu (FAB on mobile / top-corner trigger on desktop). */}
            <HeroStrip
                league={league}
                leagueId={leagueId}
                tournament={tournament}
                tournamentCount={tournamentCount}
                me={me}
                myAvatar={myParticipant?.avatar_emoji}
                myFlair={myParticipant?.crew_flair}
                isSpectator={isSpectator}
                seasonStanding={seasonStanding}
                unreadMentions={unreadMentions}
                tenantLifecycleState={data?.tenant_lifecycle_state}
                draft={draft}
                participants={participants}
                liveDraftEnabled={!!data?.feature_flags?.live_draft_enabled}
                setViewTournamentId={setViewTournamentId}
                resetLoadThrottle={resetLoadThrottle}
                onNewTournament={() => navigate(`/league/${leagueId}/setup`)}
                setTournamentCount={setTournamentCount}
                switcherRefreshKey={switcherRefreshKey}
                onOpenUserGuide={() => setUserGuideOpen(true)}
                onOpenTinyCrew={() => setCrewModalOpen(true)}
                onOpenCrewIdentity={() => setCrewIdentityOpen(true)}
                onOpenAccount={() => setEmailModalOpen(true)}
                onOpenScoring={() => setRulesOpen(true)}
                onMentionChipClick={handleMentionChipClick}
                onCopyInvite={handleCopy}
                onLogout={handleLogout}
                onOpenSetupWizard={tournamentCount === 0 ? () => navigate("/start") : null}
                copied={copied}
            />

            <main className="px-4 lg:px-12 py-6 max-w-7xl mx-auto space-y-6">
                {isSpectator && (
                    <div className="brutal-card-light p-4 flex items-center gap-3 border-cyber bg-cyber/5" data-testid="spectator-banner">
                        <BookOpen className="w-5 h-5 text-cyber shrink-0" />
                        <div className="flex-1 min-w-0 text-sm">
                            <p className="font-bold text-white">Viewing as spectator</p>
                            <p className="text-xs text-zinc-400">View-only share — leaderboard, bracket, and recent matches update in real time. To join the Tiny Crew Rivalry, ask an existing crewmate for an invite.</p>
                        </div>
                    </div>
                )}
                {/* iter86.46 — Free-tier auto-archive countdown banner.
                    iter87.469 (operator-caught) — visible to EVERY
                    crewmate, not just the admin, so the Pro+ CTA must
                    not promise something only the admin can actually
                    do (a player's own Pro+ signup doesn't upgrade the
                    rivalry — only the admin's does). */}
                {data?.league?.free_archive_at && !data?.league?.archived_at &&
                    !["paid", "comp"].includes(data?.league?.subscription_tier) && (
                    <div className="brutal-card-light p-4 flex items-center gap-3 border-blaze bg-blaze/5" data-testid="free-archive-banner">
                        <span className="text-xl shrink-0">🏁</span>
                        <div className="flex-1 min-w-0 text-sm">
                            <p className="font-bold text-white">
                                Tournament complete — this Rivalry archives in{" "}
                                {Math.max(0, Math.ceil((new Date(data.league.free_archive_at) - Date.now()) / 86400000))}{" "}
                                day{Math.ceil((new Date(data.league.free_archive_at) - Date.now()) / 86400000) === 1 ? "" : "s"}
                            </p>
                            <p className="text-xs text-zinc-400">
                                Free Rivalries run one tournament start to finish. {/* iter87.469.1 (testing_agent-caught)
                                — this used to claim archiving keeps everything viewable; it actually locks
                                EVERYONE out entirely (league_archive_guard), same as the Danger Zone's own
                                warning — don't contradict that. */}
                                Once archived, no one — including you — can view or access it anymore. {me?.is_admin ? (
                                    <>To keep the Rivalry open and add more tournaments,{" "}
                                    <Link to="/rivalry-pro" className="text-cyber hover:underline" data-testid="free-archive-banner-pro-plus-link">
                                        sign up for Pro+
                                    </Link>.</>
                                ) : (
                                    "Ask your rivalry admin to upgrade to Pro+ if you want to keep it open for more tournaments."
                                )}
                            </p>
                        </div>
                    </div>
                )}
                {/* iter87.407 — Closed (read-only) banner. Covers the Pro+
                    admin-initiated close path; free-tier closes already
                    surface via the archive-countdown banner above, so this
                    is gated off `free_archive_at` to avoid showing both. */}
                {data?.league?.closed_at && !data?.league?.archived_at && !data?.league?.free_archive_at && (
                    <div className="brutal-card-light p-4 flex items-center gap-3 border-heat bg-heat/5" data-testid="closed-rivalry-banner">
                        <Lock className="w-5 h-5 text-heat shrink-0" />
                        <div className="flex-1 min-w-0 text-sm">
                            <p className="font-bold text-white">This Rivalry is closed — read-only</p>
                            <p className="text-xs text-zinc-400">
                                No new picks, edits, or admin changes. Everything stays viewable — leaderboard,
                                recaps, and history. {data?.league?.pro_plus_ok || ["paid", "comp"].includes(data?.league?.subscription_tier)
                                    ? "The rivalry admin can reopen it anytime from Rivalry Settings."
                                    : ""}
                            </p>
                        </div>
                    </div>
                )}
                {/* Recovery email banner — only shown if participant has no email saved */}
                {me && !me.email && !emailBannerDismissed && (
                    <EmailRecoveryBanner
                        onOpenAddEmail={() => { setEmailDraft(""); setEmailModalOpen(true); }}
                        onDismiss={dismissEmailBanner}
                    />
                )}

                {/* iter87.6 — Start Draft prominent call-out (admin only,
                    per-tournament localStorage-backed dismiss). Same visual
                    family as the recovery email banner above. */}
                <StartDraftBanner
                    leagueId={leagueId}
                    tournament={tournament}
                    me={me}
                    draft={draft}
                    hasOrphanAssignments={participants?.some((p) => (p.assigned_team_ids || []).length > 0)}
                />

                {/* iter87.199.161 — Weekly Spread mid-season "Start
                    Tournament: Week X" persistent banner. */}
                <StartBoostTournamentBanner
                    tournament={tournament}
                    me={me}
                    boostState={boost.state}
                    onStarted={boost.refresh}
                />

                {/* iter86.77.17 — "Looks finished — mark it complete" nudge
                    for CUSTOM (manual-score) tournaments. Auto-hides once
                    dismissed or the tournament is completed. Admin-only. */}
                {tournament && (me?.is_admin || me?.is_co_admin) && (
                    <CompleteNudgeBanner
                        leagueId={leagueId}
                        tournament={tournament}
                        champion={champion}
                        matches={data?.matches || []}
                    />
                )}

                {/* No tournament yet */}
                {!tournament && (
                    <div className="brutal-card p-8 text-center">
                        <Trophy className="w-12 h-12 mx-auto text-cyber mb-3" strokeWidth={2.5} />
                        <h2 className="font-display font-black text-2xl mb-2">No Tournament yet</h2>
                        <p className="text-zinc-400 mb-5">
                            {me?.is_admin ? "Set up your first Tournament to get your crew rolling." : "Waiting on your admin to create the Tournament."}
                        </p>
                        {me?.is_admin && (
                            <Link to={`/league/${leagueId}/setup`} className="brutal-btn-primary inline-flex" data-testid="setup-tournament-btn">
                                Set up Tournament <ChevronRight className="w-4 h-4" />
                            </Link>
                        )}
                        <div className="mt-6 pt-6 border-t-2 border-[#333]">
                            <p className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Crewmates in league ({participants.length}/20)</p>
                            <div className="flex flex-wrap gap-2 justify-center">
                                {participants.map(p => (
                                    <span key={p.id} className="pill bg-ink border-[#333] text-white">
                                        {p.is_admin && <Crown className="w-3 h-3 text-cyber" />}
                                        {p.is_co_admin && !p.is_admin && <Shield className="w-3 h-3 text-volt" title="Co-admin" data-testid={`co-admin-badge-${p.id}`} />}
                                        {p.username}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {tournament && (
                    <>
                        {/* iter86.95 — The tournament hero card (name, status
                            pills, draft CTA) folded into the sticky HeroStrip
                            above. Feed starts directly at the pick/rank CTAs. */}

                        {/* iter87.51 — Live tickers moved ABOVE the Draft
                            strip so the "live now · score · time in game"
                            green highlight sits directly under the ribbon
                            whenever games are running. Self-hide when
                            nothing is live. */}
                        {tournament?.template === "nfl_season_api" && tournament?.id && (
                            <NflLiveTicker tournamentId={tournament.id} myTeamIds={me?.assigned_team_ids || []} onOpenSchedule={() => setView("schedule")} />
                        )}
                        {/* iter87.111 — Same live ticker for NFL Weekly
                            Spread Boost. Single-vendor (API-Sports league=1). */}
                        {tournament?.template === "nfl_boost_api" && tournament?.id && (
                            <NflLiveTicker
                                tournamentId={tournament.id}
                                sport="nfl_boost"
                                myTeamIds={(boost?.state?.my_picks || []).map((p) => p.team_id).filter(Boolean)}
                                onOpenSchedule={() => setView("schedule")}
                            />
                        )}
                        {/* iter87.212.31 — Soccer gets the same detailed
                            live-score cards as football now. iter87.359
                            (user-requested) — now sourced from
                            `tickerEntries` (live + upcoming-soon +
                            recently-finished, NFL's own ±4.5h/8h window)
                            instead of strictly-live-only entries, zero
                            extra API cost either way. */}
                        {isApiFootballTemplate(tournament?.template) && (
                            <SoccerLiveTicker
                                entries={tickerEntries}
                                myTeamIds={me?.assigned_team_ids || []}
                                onOpenSchedule={() => setView("schedule")}
                            />
                        )}
                        {/* iter87.312 (user-reported, 3rd time) — MLB/NBA
                            Playoffs get the exact same detailed "2 teams
                            stacked + score + inning/quarter" card treatment
                            as NFL/soccer instead of the plain scrolling
                            strip. `tickerEntries` (from `_bracket_schedule_
                            entries`) already carries the identical shape
                            `SoccerLiveTicker` consumes — zero backend
                            changes, zero new component needed. */}
                        {MLB_NBA_BRACKET_TEMPLATES.includes(tournament?.template) && (
                            <SoccerLiveTicker
                                entries={tickerEntries}
                                myTeamIds={myPicksForTournament(me, tournament)}
                                onOpenSchedule={() => setView("schedule")}
                            />
                        )}
                        {/* iter87.217 (user-reported) — the always-visible
                            "Series Predictor" banner card that used to sit
                            here was retired: user flagged it as an
                            unfamiliar UI pattern. Series Predictor is now
                            a proper Draft-grid IN-SEASON PICKS tile (see
                            `TodoChips.jsx`) that opens
                            `MlbSeriesPickModal`, same convention as every
                            other pick surface — no standalone card. */}
                        {/* iter87.212.31 — Scrolling strip now only shows
                            for templates WITHOUT a detailed ticker above
                            (golf, custom/GotSport, bracket knockouts).
                            Football + soccer already got the detailed
                            treatment, so the redundant scroller hides. */}
                        {liveCount > 0 && !hasDetailedLiveTicker && (
                            <LiveGameStrip
                                liveEntries={liveEntries}
                                onOpenSchedule={() => setView("schedule")}
                                template={tournament?.template}
                                isSpectator={isSpectator}
                            />
                        )}

                        {/* iter87.1 — Crew-member To-Dos strip. Elevates every
                            actionable player activity (rank teams, pot picks,
                            champion bonus, power up, matchday boost) into a
                            single horizontally-scrollable status-first band at
                            the top of the page. Chips only appear when their
                            activity currently applies to this player. */}
                        {/* iter87.180 Item E — "Don't forget" nudge for
                            non-admins, above the DRAFT (TodoChips) section.
                            Feature-aware copy (Pre-season / In-season /
                            Both). Persistent per-tournament via
                            sessionStorage (StartDraftBanner pattern —
                            reappears on re-entry). Auto-hides once first
                            regular-season game kicks off AND user's
                            pre-season picks are complete. */}
                        <DontForgetBanner
                            leagueId={leagueId}
                            tournament={tournament}
                            me={me}
                            board={board}
                            boost={boost}
                            matches={data?.matches}
                            squares={squares}
                        />

                        <TodoChips
                            me={me}
                            tournament={tournament}
                            board={board}
                            drafts_by_tournament={data?.league?.drafts_by_tournament}
                            boost={boost}
                            intel={intel}
                            playoffSeriesIntel={playoffSeriesIntel}
                            squares={squares}
                            onOpenSquares={() => setSquaresModal(true)}
                            onOpenPotPicks={() => setPotPicksModal(true)}
                            onOpenMatchdayBoost={() => setMatchdayBoostModal(true)}
                            onOpenRankTeams={() => setRankTeamsModal(true)}
                            onOpenKnockoutPick={() => setKnockoutPickModal(true)}
                            onOpenMlbChampsPick={() => setMlbChampsModal(true)}
                            onOpenMlbSeriesPick={() => setMlbSeriesModal(true)}
                            onOpenNbaChampsPick={() => setNbaChampsModal(true)}
                            onOpenNbaSeriesPick={() => setNbaSeriesModal(true)}
                            onOpenPowerPick={() => setPowerPickModal(true)}
                            onOpenRivalriesIntel={() => setRivalriesIntelModal(true)}
                            onOpenPlayoffSeriesIntel={() => setPlayoffSeriesIntelModal(true)}
                            onOpenMlsSeriesIntel={() => setMlsSeriesIntelModal(true)}
                            onOpenMlsKnockoutIntel={() => setMlsKnockoutIntelModal(true)}
                            onOpenNwslChampion={() => setNwslChampionModal(true)}
                            onOpenNwslDarkhorse={() => setNwslDarkhorseModal(true)}
                            onOpenNwslPredictor={() => setNwslPredictorModal(true)}
                            onOpenMlsChampion={() => setMlsChampionModal(true)}
                            onOpenMlsDarkhorse={() => setMlsDarkhorseModal(true)}
                            onOpenMlsPlayin={() => setMlsPlayinModal(true)}
                            onOpenMlsSeriesPick={() => setMlsSeriesModal(true)}
                            onOpenUclChampion={() => setUclChampionModal(true)}
                            onOpenUclDarkhorse={() => setUclDarkhorseModal(true)}
                            onOpenUclAdvancePick={() => setUclAdvanceModal(true)}
                            onOpenUclChampionsPredictor={() => setUclChampionsPredictorModal(true)}
                            onOpenUclTwoLegIntel={() => setUclTwoLegIntelModal(true)}
                            onOpenUclFinalIntel={() => setUclFinalIntelModal(true)}
                            onOpenNflChampion={() => setNflChampionModal(true)}
                            onOpenNflDarkhorse={() => setNflDarkhorseModal(true)}
                            onOpenCfbChampion={() => setCfbChampionModal(true)}
                            onOpenCfbDarkhorse={() => setCfbDarkhorseModal(true)}
                        />

                        {/* iter86.77.8 — NFL live score strip (moved above). */}
                        {/* iter62 — Inline live-game strip (moved above). */}

                        {/* iter87.0 — One UI: the main page IS the bento grid.
                            Status-first tiles are the navigation; each opens a
                            full plane (Takeover). Admin tile → admin bento. */}
                        <BentoHome
                            leagueId={leagueId}
                            tournament={tournament}
                            me={me}
                            board={board}
                            myRow={myRow}
                            myTeams={myTeams}
                            liveCount={liveCount}
                            wsConnected={wsConnected}
                            showStandingsTab={showStandingsTab}
                            showBracketTab={showBracketTab}
                            champion={champion}
                            roundMovers={roundMovers}
                            onOpen={setView}
                            squaresState={squares.state}
                        />

                        {/* iter87.0 — Plane container (Takeover per tile).
                            iter87.5 — My Picks plane retired; every pick /
                            boost activity now lives in its own modal opened
                            from a To-Do chip, so `picksContent` is gone. */}
                        <DashboardTabs
                            view={view}
                            onClose={() => setView(null)}
                            leagueId={leagueId}
                            tournament={tournament}
                            data={data}
                            me={me}
                            board={board}
                            myRow={myRow}
                            myTeams={myTeams}
                            isSpectator={isSpectator}
                            onTeamRenamed={load}
                        />

                        {/* iter87.2 — Permanent Smack Stream + Rivalry Messages
                            section. Same layout as before (SmackStreamCard +
                            HallOfBurn / Rivalry Messages & Updates) but lives
                            below Game Center instead of behind a tile. The
                            header carries the section label + unread badge so
                            the mention chip up top stays a signal. */}
                        <section id="smack-section" data-testid="smack-section" className="space-y-2 pt-2">
                            <div className="flex items-center justify-between px-1">
                                <p className="text-base sm:text-lg uppercase tracking-[0.18em] text-white font-bold flex items-center gap-2">
                                    <MessagesSquare className="w-5 h-5" /> Dominate
                                </p>
                                {unreadMentions > 0 && (
                                    <span
                                        data-testid="smack-section-unread-badge"
                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-neon text-black text-[10px] font-bold uppercase tracking-wider"
                                    >
                                        <span className="w-1 h-1 rounded-full bg-black animate-pulse" />
                                        {unreadMentions} unread
                                    </span>
                                )}
                            </div>
                            <DashboardFooterCards
                                leagueId={leagueId}
                                me={me}
                                isAdmin={!!me?.is_admin}
                                smackPing={smackPing}
                                recentChanges={recentChanges}
                                participants={participants}
                            />
                        </section>
                    </>
                )}
            </main>

            {/* iter63z''''''''''''' → iter87.199.68 — AdminFirstRunWizard
                retired. First-run onboarding now lives at `/start`
                (see `pages/Start.jsx`). The hamburger menu's "Show
                setup wizard" entry navigates there for admins who
                still have no tournament configured. */}
            {/* iter87.180.1 — PlayerFirstRunWizard removed. Non-admins
                now see the Rivalry Manual on first sign-in instead of
                a wizard-then-manual double pop. */}

            {/* iter63z'''''''''''' — Slice 4 of the refactor: all modal mounts
                consolidated into `<DashboardModalStack/>`. Page-level state +
                handlers flow in via props; the stack handles composition. */}
            <DashboardModalStack
                leagueId={leagueId}
                league={league}
                tournament={tournament}
                tournamentCount={tournamentCount}
                me={me}
                myRow={myRow}
                myTeams={myTeams}
                board={board}
                availableTeams={availableTeams}
                data={data}
                rulesOpen={rulesOpen}
                crewModalOpen={crewModalOpen}
                crewIdentityOpen={crewIdentityOpen}
                myParticipant={myParticipant}
                userGuideOpen={userGuideOpen}
                knockoutPickModal={knockoutPickModal}
                powerPickModal={powerPickModal}
                emailModalOpen={emailModalOpen}
                logoutConfirmOpen={logoutConfirmOpen}
                rankTeamsModal={rankTeamsModal}
                legalPrivacyOpen={legalPrivacyOpen}
                legalTermsOpen={legalTermsOpen}
                setRulesOpen={setRulesOpen}
                setCrewModalOpen={setCrewModalOpen}
                setCrewIdentityOpen={setCrewIdentityOpen}
                onIdentityUpdated={() => load()}
                setUserGuideOpen={setUserGuideOpen}
                setKnockoutPickModal={setKnockoutPickModal}
                setPowerPickModal={setPowerPickModal}
                setEmailModalOpen={setEmailModalOpen}
                setLogoutConfirmOpen={setLogoutConfirmOpen}
                setRankTeamsModal={setRankTeamsModal}
                setLegalPrivacyOpen={setLegalPrivacyOpen}
                setLegalTermsOpen={setLegalTermsOpen}
                setEmailDraft={setEmailDraft}
                onSetKnockoutPick={handleSetKnockoutPick}
                onSetPowerPick={handleSetPowerPick}
                onSaveEmail={handleSaveEmail}
                onToggleNotifications={handleToggleNotifications}
                onConfirmLogout={confirmLogout}
            />

            {/* iter87.3 — Focused chip-driven Takeovers. Each opens its
                OWN activity surface — no shared My Picks plane. */}
            <PotPicksModal
                open={potPicksModal}
                onOpenChange={setPotPicksModal}
                tournament={tournament}
                me={me}
                isLeagueAdmin={!!me?.is_admin}
                onPicked={() => load({ force: true })}
            />
            <MatchdayBoostModal
                open={matchdayBoostModal}
                onOpenChange={setMatchdayBoostModal}
                tournament={tournament}
                me={me}
                boostState={boost.state}
                onRefresh={boost.refresh}
            />
            <SquaresModal
                open={squaresModal}
                onOpenChange={setSquaresModal}
                tournament={tournament}
                me={me}
                squaresState={squares.state}
                onRefresh={squares.refresh}
            />
            {/* iter87.47 Slice C — Rivalries Intel v2 tile. Only mounts
                when the hook says we have data, avoiding a dead modal
                shell for Free-tier / dark-launch environments. */}
            <RivalriesIntelModal
                open={!!(rivalriesIntelModal && intel?.intel)}
                intel={intel?.intel}
                tournament={tournament}
                onClose={() => setRivalriesIntelModal(false)}
            />
            {/* iter87.262 — Playoff Series Rivalries Intel (MLB/NBA). Only
                mounts when the hook has data, same dead-shell avoidance
                as the weekly Rivalries Intel modal above. */}
            <PlayoffSeriesIntelModal
                open={!!(playoffSeriesIntelModal && playoffSeriesIntel?.intel)}
                intel={playoffSeriesIntel?.intel}
                onClose={() => setPlayoffSeriesIntelModal(false)}
            />
            {/* iter87.465.26 — MLS's two independently-scoped intel
                tiles, each its own modal instance + `scope` prop. */}
            <PlayoffSeriesIntelModal
                open={!!(mlsSeriesIntelModal && playoffSeriesIntel?.seriesIntel?.intel)}
                intel={playoffSeriesIntel?.seriesIntel?.intel}
                scope="series"
                onClose={() => setMlsSeriesIntelModal(false)}
            />
            <PlayoffSeriesIntelModal
                open={!!(mlsKnockoutIntelModal && playoffSeriesIntel?.knockoutIntel?.intel)}
                intel={playoffSeriesIntel?.knockoutIntel?.intel}
                scope="knockout"
                onClose={() => setMlsKnockoutIntelModal(false)}
            />
            {/* iter87.466.1 — UCL's two independently-scoped intel
                tiles, same split mechanism as MLS's above. */}
            <PlayoffSeriesIntelModal
                open={!!(uclTwoLegIntelModal && playoffSeriesIntel?.uclTwoLegIntel?.intel)}
                intel={playoffSeriesIntel?.uclTwoLegIntel?.intel}
                scope="two_leg"
                onClose={() => setUclTwoLegIntelModal(false)}
            />
            <PlayoffSeriesIntelModal
                open={!!(uclFinalIntelModal && playoffSeriesIntel?.uclFinalIntel?.intel)}
                intel={playoffSeriesIntel?.uclFinalIntel?.intel}
                scope="final"
                onClose={() => setUclFinalIntelModal(false)}
            />
            {tournament?.template === "mlb_playoffs_api" && (
                <MlbChampionsPickModal
                    open={mlbChampsModal}
                    onClose={() => setMlbChampsModal(false)}
                    onSaved={(teamId) => setMe((prev) => ({
                        ...prev,
                        mlb_champs_by_tournament: {
                            ...(prev.mlb_champs_by_tournament || {}),
                            [tournament.id]: { ws: teamId },
                        },
                    }))}
                    tournamentId={tournament.id}
                    participantId={me?.id}
                    teams={tournament?.teams || []}
                    existingPicks={(me?.mlb_champs_by_tournament || {})[tournament.id]}
                    tournament={tournament}
                />
            )}
            {tournament?.template === "mlb_playoffs_api" && (
                <MlbSeriesPickModal
                    open={mlbSeriesModal}
                    onClose={() => setMlbSeriesModal(false)}
                    tournamentId={tournament.id}
                    participantId={me?.id}
                    teams={tournament?.teams || []}
                    tournament={tournament}
                    existingPicks={(me?.mlb_series_picks_by_tournament || {})[tournament.id]}
                    onPicksChanged={(nextPicks) => setMe((prev) => ({
                        ...prev,
                        mlb_series_picks_by_tournament: {
                            ...(prev.mlb_series_picks_by_tournament || {}),
                            [tournament.id]: nextPicks,
                        },
                    }))}
                />
            )}
            {tournament?.template === "nba_playoffs_api" && (
                <NbaChampionsPickModal
                    open={nbaChampsModal}
                    onClose={() => setNbaChampsModal(false)}
                    onSaved={(teamId) => setMe((prev) => ({
                        ...prev,
                        nba_champs_by_tournament: {
                            ...(prev.nba_champs_by_tournament || {}),
                            [tournament.id]: { champion: teamId },
                        },
                    }))}
                    tournamentId={tournament.id}
                    participantId={me?.id}
                    teams={tournament?.teams || []}
                    existingPicks={(me?.nba_champs_by_tournament || {})[tournament.id]}
                    tournament={tournament}
                />
            )}
            {tournament?.template === "nba_playoffs_api" && (
                <NbaSeriesPickModal
                    open={nbaSeriesModal}
                    onClose={() => setNbaSeriesModal(false)}
                    tournamentId={tournament.id}
                    participantId={me?.id}
                    teams={tournament?.teams || []}
                    tournament={tournament}
                    existingPicks={(me?.nba_series_picks_by_tournament || {})[tournament.id]}
                    onPicksChanged={(nextPicks) => setMe((prev) => ({
                        ...prev,
                        nba_series_picks_by_tournament: {
                            ...(prev.nba_series_picks_by_tournament || {}),
                            [tournament.id]: nextPicks,
                        },
                    }))}
                />
            )}

            {tournament?.template === "nwsl_playoffs_api" && (
                <NwslChampionPickModal
                    open={nwslChampionModal}
                    onClose={() => setNwslChampionModal(false)}
                    onSaved={(teamIds) => setMe((prev) => ({
                        ...prev,
                        nwsl_champion_by_tournament: { ...(prev.nwsl_champion_by_tournament || {}), [tournament.id]: teamIds },
                    }))}
                    tournamentId={tournament.id}
                    participantId={me?.id}
                    existingPicks={(me?.nwsl_champion_by_tournament || {})[tournament.id]}
                    bonusPoints={tournament?.scoring?.nwsl_champion_points ?? 15}
                    hedgePoints={tournament?.scoring?.nwsl_champion_hedge_points ?? 5}
                />
            )}
            {tournament?.template === "nwsl_playoffs_api" && (
                <NwslDarkhorsePickModal
                    open={nwslDarkhorseModal}
                    onClose={() => setNwslDarkhorseModal(false)}
                    onSaved={(teamId) => setMe((prev) => ({
                        ...prev,
                        nwsl_darkhorse_by_tournament: { ...(prev.nwsl_darkhorse_by_tournament || {}), [tournament.id]: teamId },
                    }))}
                    tournamentId={tournament.id}
                    participantId={me?.id}
                    existingPick={(me?.nwsl_darkhorse_by_tournament || {})[tournament.id]}
                    winPoints={tournament?.scoring?.nwsl_darkhorse_win_points ?? 5}
                />
            )}
            {tournament?.template === "nwsl_playoffs_api" && (
                <NwslPredictorModal
                    open={nwslPredictorModal}
                    onClose={() => setNwslPredictorModal(false)}
                    onSaved={(nextPicks) => setMe((prev) => ({
                        ...prev,
                        nwsl_predictor_picks_by_tournament: {
                            ...(prev.nwsl_predictor_picks_by_tournament || {}),
                            [tournament.id]: { ...((prev.nwsl_predictor_picks_by_tournament || {})[tournament.id] || {}), ...nextPicks },
                        },
                    }))}
                    tournamentId={tournament.id}
                    participantId={me?.id}
                    existingPicks={(me?.nwsl_predictor_picks_by_tournament || {})[tournament.id]}
                />
            )}

            {tournament?.template === "mls_playoffs_api" && (
                <MlsChampionPickModal
                    open={mlsChampionModal}
                    onClose={() => setMlsChampionModal(false)}
                    onSaved={(teamIds) => setMe((prev) => ({
                        ...prev,
                        mls_champion_by_tournament: { ...(prev.mls_champion_by_tournament || {}), [tournament.id]: teamIds },
                    }))}
                    tournamentId={tournament.id}
                    participantId={me?.id}
                    existingPicks={(me?.mls_champion_by_tournament || {})[tournament.id]}
                    bonusPoints={tournament?.scoring?.mls_champion_points ?? 15}
                    hedgePoints={tournament?.scoring?.mls_champion_hedge_points ?? 5}
                />
            )}
            {tournament?.template === "mls_playoffs_api" && (
                <MlsDarkhorsePickModal
                    open={mlsDarkhorseModal}
                    onClose={() => setMlsDarkhorseModal(false)}
                    onSaved={(teamId) => setMe((prev) => ({
                        ...prev,
                        mls_darkhorse_by_tournament: { ...(prev.mls_darkhorse_by_tournament || {}), [tournament.id]: teamId },
                    }))}
                    tournamentId={tournament.id}
                    participantId={me?.id}
                    existingPick={(me?.mls_darkhorse_by_tournament || {})[tournament.id]}
                    winPoints={tournament?.scoring?.mls_darkhorse_win_points ?? 5}
                />
            )}
            {tournament?.template === "mls_playoffs_api" && (
                <MlsPlayinPickModal
                    open={mlsPlayinModal}
                    onClose={() => setMlsPlayinModal(false)}
                    onSaved={(nextPicks) => setMe((prev) => ({
                        ...prev,
                        mls_playin_by_tournament: { ...(prev.mls_playin_by_tournament || {}), [tournament.id]: nextPicks },
                    }))}
                    tournamentId={tournament.id}
                    participantId={me?.id}
                    existingPicks={(me?.mls_playin_by_tournament || {})[tournament.id]}
                    correctPoints={tournament?.scoring?.mls_playin_correct_points ?? 6}
                    incorrectPoints={tournament?.scoring?.mls_playin_incorrect_points ?? -2}
                />
            )}
            {tournament?.template === "mls_playoffs_api" && (
                <MlsSeriesPickModal
                    open={mlsSeriesModal}
                    onClose={() => setMlsSeriesModal(false)}
                    tournamentId={tournament.id}
                    participantId={me?.id}
                    teams={(tournament?.mls || {}).teams || []}
                    tournament={tournament}
                    existingPicks={(me?.mls_series_picks_by_tournament || {})[tournament.id]}
                    onPicksChanged={(nextPicks) => setMe((prev) => ({
                        ...prev,
                        mls_series_picks_by_tournament: {
                            ...(prev.mls_series_picks_by_tournament || {}),
                            [tournament.id]: nextPicks,
                        },
                    }))}
                />
            )}

            {tournament?.template === "ucl_playoffs_api" && (
                <UclChampionPickModal
                    open={uclChampionModal}
                    onClose={() => setUclChampionModal(false)}
                    onSaved={(teamIds) => setMe((prev) => ({
                        ...prev,
                        ucl_champion_by_tournament: { ...(prev.ucl_champion_by_tournament || {}), [tournament.id]: teamIds },
                    }))}
                    tournamentId={tournament.id}
                    participantId={me?.id}
                    existingPicks={(me?.ucl_champion_by_tournament || {})[tournament.id]}
                    bonusPoints={tournament?.scoring?.ucl_champion_points ?? 20}
                    hedgePoints={tournament?.scoring?.ucl_champion_hedge_points ?? 8}
                />
            )}
            {tournament?.template === "ucl_playoffs_api" && (
                <UclDarkhorsePickModal
                    open={uclDarkhorseModal}
                    onClose={() => setUclDarkhorseModal(false)}
                    onSaved={(teamId) => setMe((prev) => ({
                        ...prev,
                        ucl_darkhorse_by_tournament: { ...(prev.ucl_darkhorse_by_tournament || {}), [tournament.id]: teamId },
                    }))}
                    tournamentId={tournament.id}
                    participantId={me?.id}
                    existingPick={(me?.ucl_darkhorse_by_tournament || {})[tournament.id]}
                    outerPoints={tournament?.scoring?.ucl_darkhorse_outer_tier_points ?? 10}
                    innerPoints={tournament?.scoring?.ucl_darkhorse_inner_tier_points ?? 5}
                />
            )}
            {tournament?.template === "ucl_playoffs_api" && (
                <UclAdvancePickModal
                    open={uclAdvanceModal}
                    onClose={() => setUclAdvanceModal(false)}
                    tournamentId={tournament.id}
                    participantId={me?.id}
                    teams={(tournament?.ucl || {}).teams || []}
                    existingPicks={(me?.ucl_advance_picks_by_tournament || {})[tournament.id]}
                    onPicksChanged={(nextPicks) => setMe((prev) => ({
                        ...prev,
                        ucl_advance_picks_by_tournament: {
                            ...(prev.ucl_advance_picks_by_tournament || {}),
                            [tournament.id]: nextPicks,
                        },
                    }))}
                />
            )}
            {tournament?.template === "ucl_playoffs_api" && (
                <UclChampionsPredictorModal
                    open={uclChampionsPredictorModal}
                    onClose={() => setUclChampionsPredictorModal(false)}
                    onSaved={(nextPick) => setMe((prev) => ({
                        ...prev,
                        ucl_champions_predictor_by_tournament: {
                            ...(prev.ucl_champions_predictor_by_tournament || {}),
                            [tournament.id]: nextPick,
                        },
                    }))}
                    tournamentId={tournament.id}
                    participantId={me?.id}
                    existingPick={(me?.ucl_champions_predictor_by_tournament || {})[tournament.id]}
                />
            )}

            {tournament?.template === "nfl_playoffs_api" && (
                <NflChampionPickModal
                    open={nflChampionModal}
                    onClose={() => setNflChampionModal(false)}
                    onSaved={(teamIds) => setMe((prev) => ({
                        ...prev,
                        nfl_champion_picks_by_tournament: { ...(prev.nfl_champion_picks_by_tournament || {}), [tournament.id]: teamIds },
                    }))}
                    tournamentId={tournament.id}
                    participantId={me?.id}
                    existingPicks={(me?.nfl_champion_picks_by_tournament || {})[tournament.id]}
                    bonusPoints={tournament?.scoring?.nfl_champion_points ?? 20}
                    hedgePoints={tournament?.scoring?.nfl_champion_hedge_points ?? 8}
                />
            )}
            {tournament?.template === "nfl_playoffs_api" && (
                <NflDarkhorsePickModal
                    open={nflDarkhorseModal}
                    onClose={() => setNflDarkhorseModal(false)}
                    onSaved={(nextPicks) => setMe((prev) => ({
                        ...prev,
                        nfl_darkhorse_by_tournament: { ...(prev.nfl_darkhorse_by_tournament || {}), [tournament.id]: nextPicks },
                    }))}
                    tournamentId={tournament.id}
                    participantId={me?.id}
                    existingPicks={(me?.nfl_darkhorse_by_tournament || {})[tournament.id]}
                    perWinPoints={tournament?.scoring?.nfl_darkhorse_win_points ?? 4}
                />
            )}

            {tournament?.template === "cfb_playoffs_api" && (
                <CfbChampionPickModal
                    open={cfbChampionModal}
                    onClose={() => setCfbChampionModal(false)}
                    onSaved={(teamIds) => setMe((prev) => ({
                        ...prev,
                        cfb_champion_picks_by_tournament: { ...(prev.cfb_champion_picks_by_tournament || {}), [tournament.id]: teamIds },
                    }))}
                    tournamentId={tournament.id}
                    participantId={me?.id}
                    existingPicks={(me?.cfb_champion_picks_by_tournament || {})[tournament.id]}
                    bonusPoints={tournament?.scoring?.cfb_champion_points ?? 20}
                    hedgePoints={tournament?.scoring?.cfb_champion_hedge_points ?? 8}
                />
            )}
            {tournament?.template === "cfb_playoffs_api" && (
                <CfbDarkhorsePickModal
                    open={cfbDarkhorseModal}
                    onClose={() => setCfbDarkhorseModal(false)}
                    onSaved={(teamId) => setMe((prev) => ({
                        ...prev,
                        cfb_darkhorse_by_tournament: { ...(prev.cfb_darkhorse_by_tournament || {}), [tournament.id]: teamId },
                    }))}
                    tournamentId={tournament.id}
                    participantId={me?.id}
                    existingPick={(me?.cfb_darkhorse_by_tournament || {})[tournament.id]}
                    perWinPoints={tournament?.scoring?.cfb_darkhorse_win_points ?? 4}
                />
            )}

            {/* iter57 — Footer with Privacy + Terms links (visible to every signed-in user). */}
            <div className="mt-12 pt-6 border-t border-[#222] flex items-center justify-center gap-3 text-[10px] text-zinc-600">
                <button
                    type="button"
                    data-testid="footer-terms-link"
                    onClick={() => setLegalTermsOpen(true)}
                    className="hover:text-cyber underline-offset-2 hover:underline"
                >
                    Terms of Use
                </button>
                <span className="text-zinc-800">·</span>
                <button
                    type="button"
                    data-testid="footer-privacy-link"
                    onClick={() => setLegalPrivacyOpen(true)}
                    className="hover:text-cyber underline-offset-2 hover:underline"
                >
                    Privacy
                </button>
            </div>
        </div>
    );
}
